import type { FC, ReactNode } from 'react';
import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Checkbox, Tooltip } from 'antd';

import { colorConstants } from 'config/colorConstants';

import {
    CHECKBOX_TOOLTIP_OVERLAY_STYLE,
    CHECKBOX_TOOLTIP_SHADOW,
    LOCKED_CHECKBOX_TOOLTIP_PROPS,
    resolveTooltipText,
    type GenericCheckboxControlProps,
    type GenericCheckboxTooltipListProps,
} from './formControl';

const GENERIC_CHECKBOX_LOCKED_CLASS = 'generic-checkbox-locked';
const GENERIC_CHECKBOX_LABEL_HIGHLIGHT_CLASS = 'generic-checkbox-label-highlight';
const LOCKED_TOOLTIP_CHECKBOX_CENTER_X = 8;
const LOCKED_TOOLTIP_VERTICAL_OFFSET = 8;
const getDocumentBody = () => document.body;
const mergeClassNames = (...classNames: Array<string | undefined>) =>
    classNames.filter(Boolean).join(' ');

const LOCKED_CHECKBOX_INNER = `
    background: ${colorConstants.blackTransparent04} !important;
    border-color: ${colorConstants.greyAlt} !important;
    box-shadow: none !important;
`;

const LOCKED_CHECKBOX_TICK = `
    border-color: ${colorConstants.greyAlt12} !important;
    opacity: 1;
`;

const StyledCheckbox = styled(Checkbox)`
    &.ant-checkbox-wrapper {
        margin: 0;
        line-height: 1;
        flex-shrink: 0;
    }

    .ant-checkbox {
        top: 0;
        align-self: center;
    }

    .ant-checkbox::after,
    .ant-checkbox-checked::after {
        display: none !important;
        border: none !important;
        animation: none !important;
    }

    .ant-checkbox-inner {
        width: 16px;
        height: 16px;
        border-radius: 4px;
        border-color: ${colorConstants.greyAlt} !important;
        background: ${colorConstants.white};
        box-shadow: none !important;
        transition: none !important;
    }

    .ant-checkbox-checked:not(.ant-checkbox-disabled) .ant-checkbox-inner,
    .ant-checkbox-indeterminate:not(.ant-checkbox-disabled) .ant-checkbox-inner {
        background: ${colorConstants.violet} !important;
        border-color: ${colorConstants.violet} !important;
    }

    .ant-checkbox-indeterminate .ant-checkbox-inner::after {
        top: 50%;
        left: 50%;
        display: block !important;
        width: 8px !important;
        height: 2px !important;
        border: 0 !important;
        border-radius: 1px;
        background: ${colorConstants.white} !important;
        transform: translate(-50%, -50%) scale(1) !important;
        opacity: 1 !important;
    }

    .ant-checkbox-checked:not(.ant-checkbox-disabled) .ant-checkbox-inner::after {
        border-color: ${colorConstants.white} !important;
    }

    &:hover .ant-checkbox:not(.ant-checkbox-disabled) .ant-checkbox-inner,
    .ant-checkbox:not(.ant-checkbox-disabled):hover .ant-checkbox-inner,
    .ant-checkbox-input:focus + .ant-checkbox-inner,
    .ant-checkbox-input:focus-visible + .ant-checkbox-inner,
    .ant-checkbox:not(.ant-checkbox-disabled):active .ant-checkbox-inner {
        border-color: ${colorConstants.greyAlt} !important;
        box-shadow: none !important;
        outline: none !important;
    }

    &:hover .ant-checkbox-checked:not(.ant-checkbox-disabled) .ant-checkbox-inner,
    .ant-checkbox-checked:not(.ant-checkbox-disabled):hover .ant-checkbox-inner,
    .ant-checkbox-checked:not(.ant-checkbox-disabled) .ant-checkbox-input:focus + .ant-checkbox-inner,
    .ant-checkbox-checked:not(.ant-checkbox-disabled) .ant-checkbox-input:focus-visible + .ant-checkbox-inner,
    .ant-checkbox-checked:not(.ant-checkbox-disabled):active .ant-checkbox-inner,
    &:hover .ant-checkbox-indeterminate:not(.ant-checkbox-disabled) .ant-checkbox-inner,
    .ant-checkbox-indeterminate:not(.ant-checkbox-disabled):hover .ant-checkbox-inner,
    .ant-checkbox-indeterminate:not(.ant-checkbox-disabled) .ant-checkbox-input:focus + .ant-checkbox-inner,
    .ant-checkbox-indeterminate:not(.ant-checkbox-disabled) .ant-checkbox-input:focus-visible + .ant-checkbox-inner,
    .ant-checkbox-indeterminate:not(.ant-checkbox-disabled):active .ant-checkbox-inner {
        background: ${colorConstants.violet} !important;
        border-color: ${colorConstants.violet} !important;
        box-shadow: none !important;
    }

    &.ant-checkbox-wrapper-disabled:not(.${GENERIC_CHECKBOX_LOCKED_CLASS})
        .ant-checkbox-disabled.ant-checkbox-checked:not(.ant-checkbox-indeterminate)
        .ant-checkbox-inner,
    &.ant-checkbox-wrapper-disabled:not(.${GENERIC_CHECKBOX_LOCKED_CLASS})
        .ant-checkbox-disabled.ant-checkbox-indeterminate
        .ant-checkbox-inner {
        background: ${colorConstants.violet} !important;
        border-color: ${colorConstants.violet} !important;
        opacity: 1;
    }

    &.ant-checkbox-wrapper-disabled:not(.${GENERIC_CHECKBOX_LOCKED_CLASS})
        .ant-checkbox-disabled.ant-checkbox-checked:not(.ant-checkbox-indeterminate)
        .ant-checkbox-inner::after {
        border-color: ${colorConstants.white} !important;
        opacity: 1;
    }

    &.${GENERIC_CHECKBOX_LOCKED_CLASS}.ant-checkbox-wrapper-disabled
        .ant-checkbox-disabled.ant-checkbox-checked
        .ant-checkbox-inner,
    &.${GENERIC_CHECKBOX_LOCKED_CLASS}:hover
        .ant-checkbox-disabled.ant-checkbox-checked
        .ant-checkbox-inner,
    &.${GENERIC_CHECKBOX_LOCKED_CLASS}
        .ant-checkbox-disabled.ant-checkbox-checked:hover
        .ant-checkbox-inner,
    &.${GENERIC_CHECKBOX_LOCKED_CLASS}
        .ant-checkbox-disabled.ant-checkbox-checked
        .ant-checkbox-input:focus
        + .ant-checkbox-inner,
    &.${GENERIC_CHECKBOX_LOCKED_CLASS}
        .ant-checkbox-disabled.ant-checkbox-checked
        .ant-checkbox-input:focus-visible
        + .ant-checkbox-inner {
        ${LOCKED_CHECKBOX_INNER}
    }

    &.${GENERIC_CHECKBOX_LOCKED_CLASS}.ant-checkbox-wrapper-disabled
        .ant-checkbox-disabled.ant-checkbox-checked
        .ant-checkbox-inner::after,
    &.${GENERIC_CHECKBOX_LOCKED_CLASS}:hover
        .ant-checkbox-disabled.ant-checkbox-checked
        .ant-checkbox-inner::after {
        ${LOCKED_CHECKBOX_TICK}
    }

    &.${GENERIC_CHECKBOX_LOCKED_CLASS} .ant-checkbox + span {
        color: ${colorConstants.purpleFont};
        font-weight: 500;
    }

    &.${GENERIC_CHECKBOX_LOCKED_CLASS}:hover .ant-checkbox + span,
    &.${GENERIC_CHECKBOX_LOCKED_CLASS}.ant-checkbox-wrapper-disabled .ant-checkbox + span {
        color: ${colorConstants.purpleFont};
    }

    &.${GENERIC_CHECKBOX_LABEL_HIGHLIGHT_CLASS}.ant-checkbox-wrapper-checked:not(.${GENERIC_CHECKBOX_LOCKED_CLASS})
        .ant-checkbox
        + span,
    &.${GENERIC_CHECKBOX_LABEL_HIGHLIGHT_CLASS}.ant-checkbox-wrapper-checked:not(.${GENERIC_CHECKBOX_LOCKED_CLASS}):hover
        .ant-checkbox
        + span {
        color: ${colorConstants.purpleFont};
        font-weight: 500;
    }

    .ant-checkbox + span {
        padding-inline-start: 8px;
        line-height: 22px;
        color: ${colorConstants.blackTransparent88};
    }

    &:hover .ant-checkbox + span,
    &.ant-checkbox-wrapper-disabled .ant-checkbox + span {
        color: ${colorConstants.blackTransparent88};
    }
`;

type CheckboxWrapperProps = {
    wrapperClassName?: string;
    extraClassName?: string;
    children: ReactNode;
};

const CheckboxWrapper = forwardRef<HTMLSpanElement, CheckboxWrapperProps>(
    ({ wrapperClassName, extraClassName, children }, ref) => {
        const className = mergeClassNames(wrapperClassName, extraClassName);
        if (!className) return <>{children}</>;
        return (
            <span ref={ref} className={className}>
                {children}
            </span>
        );
    },
);

CheckboxWrapper.displayName = 'CheckboxWrapper';

export const GenericCheckboxTooltipList = ({ items }: GenericCheckboxTooltipListProps) => (
    <div
        className="flex w-[214px] flex-col gap-3 rounded-xl bg-white p-5"
        style={{ boxShadow: CHECKBOX_TOOLTIP_SHADOW }}
    >
        {items.map((item) => (
            <div
                key={`${item.label}-${item.value}`}
                className="flex w-full items-start justify-between text-[14px] leading-normal"
            >
                <span style={{ color: colorConstants.toolbarLabelGrey }}>{item.label}</span>
                <span style={{ color: colorConstants.blackTitle }}>{item.value}</span>
            </div>
        ))}
    </div>
);

export const GenericCheckboxControl: FC<GenericCheckboxControlProps> = ({
    checked = false,
    indeterminate = false,
    showIndeterminate = false,
    onChange,
    disabled = false,
    className,
    wrapperClassName,
    highlightLabelWhenChecked = false,
    children,
    showTooltip = false,
    tooltipText,
    staticTooltipText,
    id,
    dataTestId,
    ariaLabel,
    stopPropagation = false,
    locked = false,
}) => {
    const lockedTooltipRowRef = useRef<HTMLSpanElement>(null);
    const [lockedTooltipAlignOffset, setLockedTooltipAlignOffset] = useState<[number, number]>([
        0,
        LOCKED_TOOLTIP_VERTICAL_OFFSET,
    ]);

    const resolvedTooltipText = resolveTooltipText(staticTooltipText, tooltipText, undefined);
    const resolvedIndeterminate = showIndeterminate && indeterminate;
    const isLocked = locked && checked;
    const isDisabled = disabled || isLocked;
    const shouldShowTooltip =
        Boolean(resolvedTooltipText) && ((showTooltip && checked) || isLocked);

    useLayoutEffect(() => {
        if (!isLocked || !shouldShowTooltip) return;

        const row = lockedTooltipRowRef.current;
        if (!row) return;

        const offsetX = LOCKED_TOOLTIP_CHECKBOX_CENTER_X - row.offsetWidth / 2;
        setLockedTooltipAlignOffset([offsetX, LOCKED_TOOLTIP_VERTICAL_OFFSET]);
    }, [children, isLocked, shouldShowTooltip]);

    const checkboxClassName = mergeClassNames(
        className,
        isLocked ? GENERIC_CHECKBOX_LOCKED_CLASS : undefined,
        highlightLabelWhenChecked ? GENERIC_CHECKBOX_LABEL_HIGHLIGHT_CLASS : undefined,
    );

    const checkbox = (
        <StyledCheckbox
            id={id}
            data-testid={dataTestId}
            aria-label={ariaLabel}
            checked={checked}
            indeterminate={resolvedIndeterminate}
            disabled={isDisabled}
            className={checkboxClassName}
            onChange={(event) => onChange(event.target.checked)}
            onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
        >
            {children}
        </StyledCheckbox>
    );

    if (!shouldShowTooltip) {
        return <CheckboxWrapper wrapperClassName={wrapperClassName}>{checkbox}</CheckboxWrapper>;
    }

    if (isLocked) {
        return (
            <Tooltip
                title={resolvedTooltipText}
                {...LOCKED_CHECKBOX_TOOLTIP_PROPS}
                align={{ offset: lockedTooltipAlignOffset }}
                getPopupContainer={getDocumentBody}
            >
                <CheckboxWrapper
                    ref={lockedTooltipRowRef}
                    wrapperClassName={wrapperClassName}
                    extraClassName="inline-flex cursor-default"
                >
                    {checkbox}
                </CheckboxWrapper>
            </Tooltip>
        );
    }

    return (
        <Tooltip
            title={resolvedTooltipText}
            placement="bottom"
            showArrow={false}
            overlayClassName="checkbox-control-tooltip"
            overlayStyle={{ maxWidth: 'none' }}
            overlayInnerStyle={CHECKBOX_TOOLTIP_OVERLAY_STYLE}
            getPopupContainer={getDocumentBody}
        >
            <CheckboxWrapper
                wrapperClassName={wrapperClassName}
                extraClassName="inline-flex cursor-default"
            >
                {checkbox}
            </CheckboxWrapper>
        </Tooltip>
    );
};
